import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router'
import { RootComponent } from './RootComponent'
import { SuspendedProtectedAppLayout } from './SuspendedProtectedAppLayout'
import { NotFoundPage } from '@/shared/components/NotFoundPage'
import { RouteErrorPage } from '@/shared/components/RouteErrorPage'
import { redirectIfAuthenticated, requireAuth, requirePartnerRole } from '@/shared/lib/requireAuth'
import { requireModuleFeatureFlag } from '@/shared/lib/requireModuleFeatureFlag'
import {
  AuthForgotPasswordPage,
  AuthLayout,
  AuthInvitationAcceptPage,
  AuthLoginPage,
  AuthMagicLinkPage,
  AuthImpersonatePage,
  AuthRegisterConfirmPage,
  AuthResetPasswordPage,
  AuthSignupPage,
  BeneficiariesPage,
  CartPage,
  DropRoomPage,
  FaqPage,
  FavoritesPage,
  GdprRequestsPage,
  LotteryDrawDetailPage,
  LotteryDrawsPage,
  MeLayout,
  MyReservationDetailPage,
  MyReservationsListPage,
  MyInvoicesPage,
  InvoiceDetailPage,
  MyTicketDetailPage,
  MyTicketsListPage,
  OnboardingLayout,
  NotificationsCenterPage,
  OnboardingPage,
  PartnerScannerPage,
  ProfilePage,
  PropertiesListPage,
  PropertyDetailPage,
  PropertyOutletLayout,
  ReservationConfirmationPage,
  ReservationHubPage,
  ReservationWizardPage,
  RootLanding,
  SessionsPage,
  TicketingOfferDetailPage,
  TicketingOffersPage,
  TombolaDrawsPage,
  WaitingQueuePage,
  HighSeasonPeriodsPage,
  HighSeasonPropertiesPage,
  HighSeasonWeeksPage,
  HighSeasonWishPage,
  HighSeasonWishesTrackingPage,
  LegalPageView,
  ListingCatalogPage,
  ListingDetailPage,
  ListingEditPage,
  MyListingsPage,
  BlogCataloguePage,
  BlogDetailPage,
  GroupTripCatalogPage,
  GroupTripDetailPage,
  MyGroupTripRegistrationsPage,
  ColonySessionsPage,
  ColonySessionDetailPage,
  MyColonyRegistrationsPage,
  MatchCalendarPage,
  MatchClubCataloguePage,
  MatchDetailPage,
  MyMatchPurchaseDetailPage,
  MyMatchPurchasesListPage,
  PartnerOfferCatalogPage,
  PartnerOfferDetailPage,
  MyPartnerOfferClaimsPage,
  PartnerOfferRedeemPage,
} from '@/app/lazyPages'
import { validateListingSearch } from '@/features/reservation/components/PropertyFilters/searchParams'

const rootRoute = createRootRoute({
  component: RootComponent,
})

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthLayout,
})

const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: SuspendedProtectedAppLayout,
  beforeLoad: async ({ location }) => {
    await requireAuth({ pathname: location.pathname, search: location.search })
  },
})

const onboardingLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'onboarding-layout',
  component: OnboardingLayout,
  beforeLoad: async ({ location }) => {
    await requireAuth({ pathname: location.pathname, search: location.search })
  },
})

const indexRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  component: RootLanding,
})

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'login',
  validateSearch: (search: Record<string, unknown>): { reason?: string } => ({
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
  beforeLoad: () => {
    redirectIfAuthenticated()
  },
  component: AuthLoginPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'forgot-password',
  component: AuthForgotPasswordPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'reset-password',
  validateSearch: (search: Record<string, unknown>): { email?: string; token?: string } => ({
    email: typeof search.email === 'string' && search.email !== '' ? search.email : undefined,
    token: typeof search.token === 'string' && search.token !== '' ? search.token : undefined,
  }),
  component: AuthResetPasswordPage,
})

const signupRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'signup',
  component: AuthSignupPage,
})

const acceptInvitationRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'accept-invitation',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AuthInvitationAcceptPage,
})

const registerConfirmRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'register/confirm',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AuthRegisterConfirmPage,
})

const magicLinkRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'magic-link',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AuthMagicLinkPage,
})

const impersonateRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: 'impersonate',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AuthImpersonatePage,
})

const reservationPropertiesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/properties',
  validateSearch: validateListingSearch,
  component: PropertiesListPage,
})

const reservationRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation',
  validateSearch: validateListingSearch,
  component: PropertiesListPage,
})

/** Shared parent: `booking`, `waiting-queue`, and index reuse the same route match through Outlet. */
const reservationPropertyLayoutRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/properties/$propertyId',
  validateSearch: validateListingSearch,
  component: PropertyOutletLayout,
})

const reservationPropertyDetailRoute = createRoute({
  getParentRoute: () => reservationPropertyLayoutRoute,
  path: '/',
  component: PropertyDetailPage,
})

const reservationPropertyBookingRoute = createRoute({
  getParentRoute: () => reservationPropertyLayoutRoute,
  path: 'booking',
  component: ReservationWizardPage,
})

const reservationRequestRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/request/$propertyId',
  validateSearch: validateListingSearch,
  component: ReservationWizardPage,
})

const reservationWaitingQueueRoute = createRoute({
  getParentRoute: () => reservationPropertyLayoutRoute,
  path: 'waiting-queue',
  component: WaitingQueuePage,
})

const highSeasonPeriodsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/high-season',
  component: HighSeasonPeriodsPage,
})

const highSeasonPropertiesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/high-season/$periodId/properties',
  component: HighSeasonPropertiesPage,
})

const highSeasonWishesTrackingRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/high-season/$periodId/wishes',
  component: HighSeasonWishesTrackingPage,
})

const highSeasonWeeksRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/high-season/$periodId/properties/$propertyId/weeks',
  component: HighSeasonWeeksPage,
})

const highSeasonWishRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/high-season/$periodId/properties/$propertyId/wish',
  component: HighSeasonWishPage,
})

const highSeasonDropRoomRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/high-season/$periodId/drop-room',
  component: DropRoomPage,
})

const reservationConfirmationRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reservation/confirmation/$reservationId',
  component: ReservationConfirmationPage,
})

const ticketingOffersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/ticketing',
  component: TicketingOffersPage,
})

const ticketingOfferDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/ticketing/offers/$offerId',
  component: TicketingOfferDetailPage,
})

const ticketingRequestRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/ticketing/request/$offerId',
  component: TicketingOfferDetailPage,
})

const listingCatalogRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/listings',
  beforeLoad: () => requireModuleFeatureFlag('listing'),
  component: ListingCatalogPage,
})

const listingDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/listings/$listingId',
  beforeLoad: () => requireModuleFeatureFlag('listing'),
  component: ListingDetailPage,
})

const myListingsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/listings/mine',
  beforeLoad: () => requireModuleFeatureFlag('listing'),
  component: MyListingsPage,
})

const listingEditRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/listings/$listingId/edit',
  beforeLoad: () => requireModuleFeatureFlag('listing'),
  component: ListingEditPage,
})

const blogCatalogueRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/blog',
  beforeLoad: () => requireModuleFeatureFlag('blog'),
  component: BlogCataloguePage,
})

const blogDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/blog/$slug',
  beforeLoad: () => requireModuleFeatureFlag('blog'),
  component: BlogDetailPage,
})

const groupTripCatalogRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/group-trips',
  beforeLoad: () => requireModuleFeatureFlag('group-trip'),
  component: GroupTripCatalogPage,
})

const groupTripDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/group-trips/$groupTripId',
  beforeLoad: () => requireModuleFeatureFlag('group-trip'),
  component: GroupTripDetailPage,
})

const colonySessionsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/colony',
  beforeLoad: () => requireModuleFeatureFlag('colony'),
  component: ColonySessionsPage,
})

const colonySessionDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/colony/sessions/$sessionId',
  beforeLoad: () => requireModuleFeatureFlag('colony'),
  component: ColonySessionDetailPage,
})

const matchCalendarRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/match-event',
  validateSearch: (search: Record<string, unknown>): { clubId?: string } => ({
    clubId: typeof search.clubId === 'string' ? search.clubId : undefined,
  }),
  beforeLoad: () => requireModuleFeatureFlag('match-event'),
  component: MatchCalendarPage,
})

const matchClubCatalogueRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/match-event/clubs',
  beforeLoad: () => requireModuleFeatureFlag('match-event'),
  component: MatchClubCataloguePage,
})

const matchDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/match-event/$matchId',
  beforeLoad: () => requireModuleFeatureFlag('match-event'),
  component: MatchDetailPage,
})

const partnerOfferCatalogRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/partner-offers',
  beforeLoad: () => requireModuleFeatureFlag('partner-offer'),
  component: PartnerOfferCatalogPage,
})

const partnerOfferDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/partner-offers/$offerId',
  beforeLoad: () => requireModuleFeatureFlag('partner-offer'),
  component: PartnerOfferDetailPage,
})

// Public, unauthenticated (SEC-M1) - mirrors reservationHubRoute, parented directly to rootRoute
// (NOT protectedLayoutRoute/authLayoutRoute): a one-time link handed to the partner, no CSE account.
const partnerOfferRedeemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partner-offers/redeem/$token',
  component: PartnerOfferRedeemPage,
})

const lotteryDrawsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/lottery',
  beforeLoad: () => requireModuleFeatureFlag('lottery'),
  component: LotteryDrawsPage,
})

const tombolaDrawsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/lottery/tombola',
  beforeLoad: () => requireModuleFeatureFlag('lottery'),
  component: TombolaDrawsPage,
})

const lotteryDrawDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/lottery/draws/$drawId',
  beforeLoad: () => requireModuleFeatureFlag('lottery'),
  component: LotteryDrawDetailPage,
})

const meLayoutRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/me',
  component: MeLayout,
})

const meIndexRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/',
  component: ProfilePage,
})

const meReservationsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'reservations',
  component: MyReservationsListPage,
})

const meGroupTripsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'group-trips',
  beforeLoad: () => requireModuleFeatureFlag('group-trip'),
  component: MyGroupTripRegistrationsPage,
})

const meColonyRegistrationsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'colony-registrations',
  beforeLoad: () => requireModuleFeatureFlag('colony'),
  component: MyColonyRegistrationsPage,
})

const mePartnerOfferClaimsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'partner-offers',
  beforeLoad: () => requireModuleFeatureFlag('partner-offer'),
  component: MyPartnerOfferClaimsPage,
})

const meFavoritesRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'favorites',
  component: FavoritesPage,
})

const meReservationDetailRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'reservations/$reservationId',
  component: MyReservationDetailPage,
})

const meInvoicesRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'invoices',
  component: MyInvoicesPage,
})

const meInvoiceDetailRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'invoices/$invoiceId',
  component: InvoiceDetailPage,
})

const meTicketsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'tickets',
  component: MyTicketsListPage,
})

const meTicketDetailRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'tickets/$purchaseId',
  component: MyTicketDetailPage,
})

const meMatchTicketsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'match-tickets',
  beforeLoad: () => requireModuleFeatureFlag('match-event'),
  component: MyMatchPurchasesListPage,
})

const meMatchTicketDetailRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'match-tickets/$purchaseId',
  beforeLoad: () => requireModuleFeatureFlag('match-event'),
  component: MyMatchPurchaseDetailPage,
})

const meSessionsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'sessions',
  component: SessionsPage,
})

const meGdprRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'gdpr',
  component: GdprRequestsPage,
})

const meBeneficiariesRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'beneficiaries',
  component: BeneficiariesPage,
})

/** Legacy bookmark / broken link → dedicated beneficiaries page. */
const meProfileRedirectRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: 'profile',
  beforeLoad: () => {
    throw redirect({ to: '/me/beneficiaries' })
  },
  component: () => null,
})

const legalPageRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/legal/$legalSlug',
  component: LegalPageView,
})

const notificationsCenterRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/notifications',
  component: NotificationsCenterPage,
})

const cartRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/cart',
  validateSearch: (search: Record<string, unknown>): { checkout?: 'success' | 'cancel' } => {
    const raw = search.checkout
    if (raw === 'success' || raw === 'cancel') {
      return { checkout: raw }
    }
    return {}
  },
  component: CartPage,
})

const onboardingRoute = createRoute({
  getParentRoute: () => onboardingLayoutRoute,
  path: '/onboarding',
  component: OnboardingPage,
})

const faqRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/faq',
  component: FaqPage,
})

const partnerScannerRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/partner/scan',
  beforeLoad: requirePartnerRole,
  component: PartnerScannerPage,
})

const reservationHubRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/r/$token',
  component: ReservationHubPage,
})

const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([
    loginRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    signupRoute,
    acceptInvitationRoute,
    registerConfirmRoute,
    magicLinkRoute,
    impersonateRoute,
  ]),
  reservationHubRoute,
  partnerOfferRedeemRoute,
  onboardingLayoutRoute.addChildren([onboardingRoute]),
  protectedLayoutRoute.addChildren([
    indexRoute,
    meLayoutRoute.addChildren([
      meIndexRoute,
      meReservationDetailRoute,
      meReservationsRoute,
      meGroupTripsRoute,
      meColonyRegistrationsRoute,
      mePartnerOfferClaimsRoute,
      meInvoicesRoute,
      meInvoiceDetailRoute,
      meFavoritesRoute,
      meTicketDetailRoute,
      meTicketsRoute,
      meMatchTicketDetailRoute,
      meMatchTicketsRoute,
      meSessionsRoute,
      meGdprRoute,
      meBeneficiariesRoute,
      meProfileRedirectRoute,
    ]),
    reservationRoute,
    reservationPropertiesRoute,
    reservationPropertyLayoutRoute.addChildren([
      reservationPropertyDetailRoute,
      reservationPropertyBookingRoute,
      reservationWaitingQueueRoute,
    ]),
    reservationRequestRoute,
    highSeasonPeriodsRoute,
    highSeasonPropertiesRoute,
    highSeasonWishesTrackingRoute,
    highSeasonWeeksRoute,
    highSeasonWishRoute,
    highSeasonDropRoomRoute,
    reservationConfirmationRoute,
    ticketingOffersRoute,
    ticketingOfferDetailRoute,
    ticketingRequestRoute,
    listingCatalogRoute,
    myListingsRoute,
    listingDetailRoute,
    listingEditRoute,
    blogCatalogueRoute,
    blogDetailRoute,
    groupTripCatalogRoute,
    groupTripDetailRoute,
    colonySessionsRoute,
    colonySessionDetailRoute,
    matchCalendarRoute,
    matchClubCatalogueRoute,
    matchDetailRoute,
    partnerOfferCatalogRoute,
    partnerOfferDetailRoute,
    lotteryDrawsRoute,
    tombolaDrawsRoute,
    lotteryDrawDetailRoute,
    legalPageRoute,
    notificationsCenterRoute,
    cartRoute,
    faqRoute,
    partnerScannerRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteErrorPage,
  defaultNotFoundComponent: NotFoundPage,
} as any) // eslint-disable-line @typescript-eslint/no-explicit-any -- aggregated routeTree with nested layouts

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

